import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * Reusable expandable card component
 * Used in DisciplineRules and RoleManager
 *
 * Matches rules page design:
 * - Header text: text-lg (18px) for titles
 * - Body text: text-base (16px) for content
 *
 * @param {boolean} expanded - Controlled expansion state (optional)
 * @param {function} onToggle - Callback when header is clicked (optional)
 * @param {node} header - Content to render in the clickable header
 * @param {string} headerAriaLabel - Accessible label for a button-style header
 * @param {node} actions - Optional actions/menu to render on the right side of header
 * @param {node} children - Content to render when expanded
 * @param {string} className - Additional classes for the card container
 */
export default function ExpandableCard({
  expanded: controlledExpanded,
  onToggle,
  header,
  headerAriaLabel,
  actions,
  children,
  className = '',
  style,
  flat = false,
  keepMounted = false,
}) {
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Use controlled if provided, otherwise use internal state
  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const elevation = flat ? 'border border-base-300' : 'shadow-md';
  const HeaderElement = headerAriaLabel && !actions ? 'button' : 'div';

  return (
    <div className={`card bg-base-100 ${elevation} ${className}`} style={style}>
      {/* Clickable Header */}
      <HeaderElement
        {...(HeaderElement === 'button' ? {
          type: 'button',
          'aria-expanded': isExpanded,
          'aria-label': headerAriaLabel,
        } : {})}
        className="card-body w-full cursor-pointer p-4 text-left transition-colors hover:bg-base-200/50"
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex-1 min-w-0">
            {header}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <motion.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRightIcon className="w-5 h-5 text-base-content/50" />
            </motion.div>
            {actions && (
              <div onClick={(e) => e.stopPropagation()}>
                {actions}
              </div>
            )}
          </div>
        </div>
      </HeaderElement>

      {/* Collapsible Content */}
      {keepMounted ? <div hidden={!isExpanded} className="border-t border-base-300">{children}</div> : <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-base-300">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>}
    </div>
  );
}
